"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import Link from "next/link";
import { chains, type Chain } from "@/data/chains";

const CATEGORIES = ["All", "L1", "L2", "EVM", "non-EVM"] as const;
type Category = typeof CATEGORIES[number];

function ChainLogo({ chain }: { chain: Chain }) {
  if (chain.logoSrc) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 p-1.5 transition-colors group-hover:bg-white/10">
        <Image
          src={chain.logoSrc}
          alt=""
          width={28}
          height={28}
          className="h-7 w-7 object-contain"
          unoptimized
        />
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-purple/15 text-sm font-bold text-brand-purple-light transition-colors group-hover:bg-brand-purple/25">
      {chain.name.charAt(0)}
    </div>
  );
}

function ChainCardInner({ chain }: { chain: Chain }) {
  return (
    <div className="flex h-full flex-col justify-between rounded-2xl border border-border-subtle bg-bg-card p-5 transition-all duration-200 group-hover:-translate-y-1 group-hover:border-brand-purple/30 group-active:scale-[0.98]">
      <div>
        <div className="mb-4 flex items-start justify-between">
          <ChainLogo chain={chain} />
          <span className="rounded border border-border-subtle bg-bg-surface px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-text-muted transition-colors group-hover:border-brand-purple/20">
            {chain.category}
          </span>
        </div>
        <h3 className="mb-1.5 text-base font-semibold text-text-primary transition-colors group-hover:text-brand-purple-light">
          {chain.name}
        </h3>
        <p className="text-sm leading-relaxed text-text-secondary line-clamp-2">
          {chain.description}
        </p>
        {!chain.hasPage && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-text-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-purple-light/70" aria-hidden />
            Supported
          </p>
        )}
      </div>
    </div>
  );
}

export default function ChainsEcosystemClient() {
  const [active, setActive] = useState<Category>("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let result = chains;
    if (active !== "All") {
      result = result.filter((c) => c.category?.toLowerCase().includes(active.toLowerCase()));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    return result;
  }, [active, search]);

  return (
    <section className="section-pad">
      <Container>
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold sm:text-5xl">
            The whole ecosystem, one platform
          </h1>
          <p className="mx-auto max-w-xl text-lg text-text-secondary">
            From Ethereum to Solana to Cosmos: deploy production-grade nodes across 60+ chains.
          </p>
        </div>

        {/* Search + Filter */}
        <div className="mb-8 flex flex-col items-center gap-4">
          <input
            type="search"
            placeholder="Search chains…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-sm rounded-lg border border-border-subtle bg-bg-card px-4 py-3 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple/40"
          />
          <div className="flex flex-wrap items-center justify-center gap-2" role="tablist" aria-label="Filter chains by category">
            {CATEGORIES.map((cat) => {
              const isActive = active === cat;
              return (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(cat)}
                  className={`focus-ring cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? "border-brand-purple/60 bg-brand-purple/10 text-brand-purple-light"
                      : "border-border-subtle bg-bg-card text-text-secondary hover:border-brand-purple/40 hover:bg-brand-purple/5 hover:text-text-primary"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
            <span className="ml-2 inline-flex h-9 items-center rounded-full border border-border-subtle px-3 text-sm text-text-muted">
              {filtered.length} chain{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((chain) =>
            chain.hasPage ? (
              <Link key={chain.slug} href={`/chains/${chain.slug}`} className="group">
                <ChainCardInner chain={chain} />
              </Link>
            ) : (
              <div key={chain.slug} className="group cursor-default">
                <ChainCardInner chain={chain} />
              </div>
            ),
          )}
        </div>

        {filtered.length === 0 && (
          <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-border-medium py-16 text-center">
            <p className="text-lg font-medium text-text-primary">No chains found</p>
            <p className="text-sm text-text-muted">Try adjusting your search or filters.</p>
            <button
              onClick={() => { setSearch(""); setActive("All"); }}
              className="rounded-lg border border-border-medium bg-bg-surface px-5 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 flex flex-col items-center gap-4 rounded-2xl border border-border-subtle bg-bg-surface p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-purple/25 bg-brand-purple/10">
            <span className="text-xl text-brand-purple-light">+</span>
          </div>
          <h3 className="text-xl font-bold text-text-primary">Don&apos;t see your chain?</h3>
          <p className="max-w-md text-sm text-text-secondary">Our routing layer keeps expanding. Request a chain and our team will evaluate deployment.</p>
          <Button href="/contact-us" variant="primary">Request a Chain</Button>
        </div>
      </Container>
    </section>
  );
}
